import _truncate from "lodash/truncate";
import type {
  ComponentPropsWithoutRef,
  FC,
  MouseEvent,
  CSSProperties,
} from "react";
import { useMemo, useRef } from "react";
import Highlighter from "react-highlight-words";
import { CSSTransition } from "react-transition-group";
import { FixedSizeList as List } from "react-window";
// import { ReactComponent as SearchPlaceholder } from "../../../assets/searchPlaceholder.svg";
import { animationDuration } from "../../../NavigationBar/theme";
import {
  useAccountSwitcherContext,
  type AccountSwitcherContextValue,
} from "../../AccountSwitcherContext";
import {
  StyledSearchResultWrapper,
  StyledSearchPlaceholder,
  StyledSearchResultItem,
  searchWrapperMaxHeight,
  wrapperPadding,
  itemHeight,
} from "./styled";
import "@nectary/components/tooltip";

const SearchPlaceholder = () => <></>;

const PrepareSearchResultRow = ({
  searchAccounts,
  onAccountChange,
  flattenAccountList,
  searchValue,
}: {
  searchAccounts: AccountSwitcherContextValue["searchAccounts"];
  onAccountChange: AccountSwitcherContextValue["onAccountChange"];
  flattenAccountList: AccountSwitcherContextValue["flattenAccountList"];
  searchValue: AccountSwitcherContextValue["searchValue"];
}) => {
  const SearchResultRow = ({
    index,
    style,
  }: {
    index: number;
    style: CSSProperties;
  }) => {
    const accountName = searchAccounts[index] || "";
    const onAccountClick = (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      const account = flattenAccountList[accountName] || {
        accountId: "",
        accountName: "",
      };
      onAccountChange?.({
        accountId: account.accountId,
        accountName: account.accountName.at(-1) ?? "",
      });
    };

    const formattedName = accountName
      .split(" / ")
      .map((name, idx, arr) => {
        if (idx < arr.length - 1)
          return _truncate(name, {
            length: 8, // including the `omission` characters
            omission: "...",
          });
        return name;
      })
      .join(" / ");

    return (
      <div style={style}>
        <sinch-tooltip text={accountName} orientation={"top"}>
          <StyledSearchResultItem
            key={accountName}
            onClick={onAccountClick}
            role="option"
          >
            <Highlighter
              searchWords={searchValue.split(" ")}
              highlightTag="b"
              textToHighlight={formattedName}
            />
          </StyledSearchResultItem>
        </sinch-tooltip>
      </div>
    );
  };
  return SearchResultRow;
};

export type AccountSwitcherSearchResultProps = ComponentPropsWithoutRef<"div">;

export const AccountSwitcherSearchResult: FC<
  AccountSwitcherSearchResultProps
> = ({ ...props }) => {
  const {
    isSearchBarFocus,
    searchAccounts,
    searchValue,
    onAccountChange,
    flattenAccountList,
  } = useAccountSwitcherContext();
  const nodeRef = useRef<HTMLDivElement>(null);
  const actualHeight = searchAccounts.length * itemHeight;

  const SearchResultRow = useMemo(
    () =>
      PrepareSearchResultRow({
        searchValue,
        searchAccounts,
        onAccountChange,
        flattenAccountList,
      }),
    [searchValue, searchAccounts, onAccountChange, flattenAccountList]
  );

  return (
    <CSSTransition
      in={isSearchBarFocus}
      nodeRef={nodeRef}
      timeout={animationDuration.fast}
    >
      <StyledSearchResultWrapper ref={nodeRef} {...props}>
        <StyledSearchPlaceholder hidePlaceholder={!!searchAccounts.length}>
          <SearchPlaceholder />
        </StyledSearchPlaceholder>
        {!!searchAccounts.length && isSearchBarFocus && (
          <List
            height={
              actualHeight > searchWrapperMaxHeight
                ? /* istanbul ignore next */
                  searchWrapperMaxHeight - wrapperPadding
                : actualHeight - wrapperPadding
            }
            itemCount={searchAccounts.length}
            itemSize={43}
            width={400}
          >
            {SearchResultRow}
          </List>
        )}
      </StyledSearchResultWrapper>
    </CSSTransition>
  );
};
